import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, BookOpen, FileText, CheckSquare, ChevronRight } from 'lucide-react';
import { searchAPI } from '@/lib/api';

const TYPE_ICONS = {
  module: BookOpen,
  learning: FileText,
  template: FileText,
  checklist: CheckSquare,
};

/**
 * GlobalSearch - A search bar that searches across all module content
 * via the backend /api/search endpoint (title, description, sections, SOPs, checklists).
 */
export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Debounced backend search
  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await searchAPI.search(query.trim());
        setResults((res.data || []).slice(0, 12));
      } catch (err) {
        console.error('Search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [query]);

  const handleResultClick = (result) => {
    navigate(`/modules/${result.module_id}`);
    onClose();
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div 
        className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search modules, topics, templates..."
            className="flex-1 text-base outline-none placeholder:text-slate-400"
            data-testid="global-search-input"
          />
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="p-4 text-center text-slate-400 text-sm">
              Searching...
            </div>
          )}
          
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="p-8 text-center">
              <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No results found for "{query}"</p>
              <p className="text-sm text-slate-400 mt-1">Try different keywords</p>
            </div>
          )}
          
          {!loading && results.length > 0 && (
            <div className="py-2">
              {results.map((result, idx) => {
                const Icon = TYPE_ICONS[result.type] || FileText;
                return (
                  <button
                    key={idx}
                    onClick={() => handleResultClick(result)}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`search-result-${idx}`}
                  >
                    <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 uppercase">
                          Module {result.module_id}
                        </span>
                        {result.module_title && result.type !== 'module' && (
                          <>
                            <ChevronRight className="w-3 h-3 text-slate-300" />
                            <span className="text-xs text-slate-400 truncate">
                              {result.module_title}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="font-medium text-slate-900 truncate">{result.title}</div>
                      {result.snippet && (
                        <p className="text-sm text-slate-500 truncate mt-0.5">{result.snippet}</p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-2" />
                  </button>
                );
              })}
            </div>
          )}
          
          {!query && (
            <div className="p-6 text-center">
              <p className="text-slate-500 text-sm">Start typing to search across all modules</p>
              <p className="text-slate-400 text-xs mt-1">Press ESC to close</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
